import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import deepmerge from 'deepmerge';
import { Box, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import useNotification from '../../../core/hooks/use_notification';
import { deleteIndoorPartnerNotificationData, getIndoorPartnersNotificationData } from '../../../core/utils/notification_utils';
import PaginationControls from '../../common/pagination_controls';
import { getDefaultPaginationQueryValues } from '../../../core/utils/api_utils';
import { GET_ROUTE_INDOOR_PARTNER_EDIT, ROUTE_INDOOR_PARTNER_NEW } from '../../../core/constants';
import { getIndoorPartnersAction, getIndoorPartnersState } from '../../../features/pod/get_indoor_partners';
import { Button, DeleteButton } from '../../common/button';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import { deleteIndoorPartnerAction, deleteIndoorPartnerState } from '../../../features/pod/delete_indoor_partner';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    name: ''
};

const IndoorPartnersList = ({ tab }) => {
    const dispatch = useDispatch();
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();

    const getIndoorPartners = useSelector(getIndoorPartnersState);
    const deleteIndoorPartner = useSelector(deleteIndoorPartnerState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    const refreshList = useCallback(() => {
        dispatch(getIndoorPartnersAction(queryParams))
            .then(response => showErrorMessage(getIndoorPartnersNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    const onDeleteIndoorPartner = (partnerId) => {
        dispatch(deleteIndoorPartnerAction(partnerId))
            .then(response => {
                showNotification(deleteIndoorPartnerNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            });
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 } }>
            <Box sx={ { display: 'flex', justifyContent: 'flex-end' } }>
                <Button onClick={ () => navigate(ROUTE_INDOOR_PARTNER_NEW, { state: { tab } }) }>
                    Dodaj partnera handlowego
                </Button>
            </Box>

            { getIndoorPartners?.loading && <LinearProgress/> }

            { getIndoorPartners?.data?.content && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Nazwa</TableCell>
                                    <TableCell>Adres</TableCell>
                                    <TableCell align="center" sx={ { width: 120 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getIndoorPartners?.data?.content?.map(indoorPartner => (
                                        <TableRow key={ indoorPartner?.partnerId } hover>
                                            <TableCell sx={ { fontWeight: 500 } }>{ indoorPartner?.name }</TableCell>
                                            <TableCell>{ indoorPartner?.address }</TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" justifyContent="center" spacing={ 1 }>
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        title="Edytuj"
                                                        onClick={ () => navigate(GET_ROUTE_INDOOR_PARTNER_EDIT(indoorPartner?.partnerId), { state: { tab } }) }
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                    <DeleteButton
                                                        onDeleteClick={ () => onDeleteIndoorPartner(indoorPartner?.partnerId) }
                                                        isLoading={ deleteIndoorPartner?.loading }
                                                    />
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                                { (!getIndoorPartners?.data?.content || getIndoorPartners?.data?.content.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={ 3 } align="center"
                                                   sx={ { py: 4, color: 'text.secondary' } }>
                                            Brak wyników do wyświetlenia.
                                        </TableCell>
                                    </TableRow>
                                ) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getIndoorPartners?.data?.pageable?.pageNumber }
                            totalPages={ getIndoorPartners?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default IndoorPartnersList;